import { equals, includes } from 'ramda';
import type { Location, NodeEntry, TextUnit } from 'slate';
import { Editor, Node, Path, Point, Range } from 'slate';

import type { RangeEdge, SelBoundaryNode } from '../internal';
import {
  FooterType,
  HeaderType,
  isInvisibleElement,
  nodeIsSelBoundary,
  Queries,
  SelEventSource,
} from '../internal';

const locationQueries = {
  /**
   * Version of Path.compare but has a special case for when Path.compare === 0.
   */
  comparePaths: (a: Path, b: Path): number => {
    const num = Path.compare(a, b);
    // Paths are in the same tree.  We want the longer/deeper path sorted last as it occurs later in the document.
    if (num === 0) return a.length - b.length;

    return num;
  },
  /**
   * Returns a full range for the editor's nodes.
   */
  fullEditorRange: (editor: Editor): Range => {
    return Editor.range(editor, []);
  },

  /**
   * Returns the path *relative* to its parent.
   */
  getPathRelativeToParent(path: Path): Path {
    return [path[path.length - 1]];
  },

  /**
   * Whether both endpoints of `range` still resolve to existing paths in the editor
   */
  hasRange: (editor: Editor, range: Range): boolean => {
    const { anchor, focus } = range;
    return Editor.hasPath(editor, anchor.path) && Editor.hasPath(editor, focus.path);
  },

  /**
   * A node's index within its parent — the last segment of its path. The root
   * path is the only path without one, so we throw rather than return a bogus
   * index; callers here resolve real node paths, so hitting it means a bug upstream.
   */
  indexInParent(path: Path): number {
    const index = path.at(-1);
    if (index === undefined) throw new Error('The root path has no parent index');
    return index;
  },

  /**
   * Check if a point is before an invisible node, in which case the point is invalid.
   */
  isPointBeforeInvisible: (editor: Editor, point: Point) => {
    if (!Editor.isEnd(editor, point, point.path)) return false;
    const [nextNode] = Queries.getNextNodeEntry(editor, point.path) ?? [null];
    return nextNode ? isInvisibleElement(nextNode) : false;
  },

  /**
   * Get the path or range to be deleted for a given location, distance, unit and reverse.
   */
  locationToDelete(
    editor: Editor,
    options: {
      at?: Location;
      distance?: number;
      reverse?: boolean;
      unit?: TextUnit;
    } = {}
  ): Range | Path | null {
    const { distance = 1, reverse = false, unit = 'character' } = options;
    let { at = editor.selection } = options;
    if (!at) return null;

    if (Path.isPath(at)) return at;

    // Treat a collapsed selection as a point
    if (Range.isRange(at) && Range.isCollapsed(at)) at = at.anchor;

    if (Point.isPoint(at)) {
      const opts = { distance, unit };
      const target = reverse
        ? Editor.before(editor, at, opts) || Editor.start(editor, [])
        : Editor.after(editor, at, opts) || Editor.end(editor, []);
      return { anchor: at, focus: target };
    }

    return at;
  },

  /**
   * Determines whether a new selection location that may contain part or all of a new selection crosses a selection boundary.
   * Useful for determining whether a new selection would make an old selection cross a selection boundary (e.g., the selection moves from document to a footer).
   */
  newSelLocationCrossesBoundary: (
    editor: Editor,
    options: { newSelLocation: Location }
  ): boolean => {
    const { newSelLocation } = options;

    const oldSelBoundaryEntry = Queries.selectionBoundaryAboveLoc(editor);
    const newSelBoundaryEntry = Queries.selectionBoundaryAboveLoc(editor, {
      at: newSelLocation,
    });

    if (!oldSelBoundaryEntry || !newSelBoundaryEntry) return false;

    const [oldSelBoundaryNode] = oldSelBoundaryEntry;
    const [newSelBoundaryNode] = newSelBoundaryEntry;

    return oldSelBoundaryNode.type !== newSelBoundaryNode.type;
  },

  /**
   * Returns a boolean indicating whether a new selection that is about to be applied is valid.
   */
  newSelectionValid: (
    editor: Editor,
    options: { eventSource: SelEventSource; newSel: Range }
  ): boolean => {
    const { eventSource, newSel } = options;

    const selBoundaryEntry = Queries.selectionBoundaryAboveLoc(editor, { at: newSel });

    // If the new selection is across boundaries, it isn't valid.
    if (!selBoundaryEntry) return false;

    const [selBoundaryNode] = selBoundaryEntry;
    const headerFooterTypes = [...Object.values(FooterType), ...Object.values(HeaderType)];
    const activeHeaderFooterTypes = Queries.activeHeaderOrFooterTypes(editor);

    // If the new selection is inside a non-active header/footer, it isn't valid.
    if (includes(selBoundaryNode.type, headerFooterTypes)) {
      const boundaryNodeNotActiveHeaderFooter = !includes(
        selBoundaryNode.type,
        activeHeaderFooterTypes
      );

      if (boundaryNodeNotActiveHeaderFooter) return false;
    }

    /**
     * The new selection is inside a consistent boundary node.  But, the selection shouldn't cross boundaries unless it is
     * from a mouse event.
     */
    switch (eventSource) {
      case SelEventSource.MOUSE:
      case SelEventSource.PROGRAMMATIC:
        return true;
      case SelEventSource.KEYBOARD:
        return !Queries.newSelLocationCrossesBoundary(editor, { newSelLocation: newSel });
    }
  },

  /**
   * Given an edge of a Range, returns to you whether the edge is the anchor or focus.
   */
  rangeEdge: (args: { edge: RangeEdge; range: Range }): 'anchor' | 'focus' => {
    const { edge, range } = args;

    switch (edge) {
      case 'start':
        return Range.isBackward(range) ? 'focus' : 'anchor';
      case 'end':
        return Range.isBackward(range) ? 'anchor' : 'focus';
      default:
        return edge;
    }
  },

  /**
   * Given an edge of a Range, returns to you whether the edge is the start or end.
   */
  rangeEdgeDirectional: (args: { edge: RangeEdge; range: Range }): 'start' | 'end' => {
    const { edge, range } = args;

    switch (edge) {
      case 'anchor':
        return Range.isBackward(range) ? 'end' : 'start';
      case 'focus':
        return Range.isBackward(range) ? 'start' : 'end';
      default:
        return edge;
    }
  },

  /**
   * Returns whether anotherRange is equal to range.  Exact means the anchor/focus has to line up exactly, whereas
   * exact false means doesn't account for range direction and just means the start/end of the ranges have to line up.
   */
  rangeEquals: (range: Range, anotherRange: Range, options: { exact?: boolean } = {}): boolean => {
    const { exact = true } = options;

    if (exact) return Range.equals(range, anotherRange);

    const rangeEdges = Range.edges(range);
    const anotherEdges = Range.edges(anotherRange);

    return equals(rangeEdges, anotherEdges);
  },

  /**
   * Returns a boolean indicating whether range points are directly abutting.
   */
  rangePointsAbutting: (editor: Editor, range: Range): boolean => {
    const [start, end] = Range.edges(range);
    const startLeaf = Node.leaf(editor, start.path);
    const startEndPathsContiguous = Queries.pathIsAbutting(editor, start.path, end.path);
    const acrossParagraphs = Queries.rangeAcrossParagraphs(editor, { at: range });

    return (
      !acrossParagraphs &&
      startEndPathsContiguous &&
      start.offset === startLeaf.text.length &&
      end.offset === 0
    );
  },

  /**
   * Get the selection boundary above a location.  A selection boundary is a node boundary we don't want the selection to cross.
   */
  selectionBoundaryAboveLoc: (
    editor: Editor,
    options: { at?: Location } = {}
  ): NodeEntry<SelBoundaryNode> | undefined => {
    const { at = editor.selection } = options;

    if (!at) return;

    return Editor.above<SelBoundaryNode>(editor, {
      at,
      match: nodeIsSelBoundary,
      mode: 'lowest',
    });
  },
};

export default locationQueries;
